'use client'

import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { CreditCard, Calendar } from 'lucide-react'
import { type Shop } from '@/lib/api/shops'
import { cn } from '@/lib/utils'

interface SubscriptionStatusProps {
  shop: Shop | null
  shopId?: string
  className?: string
  onShopUpdate?: (shop: Shop) => void
}

export function SubscriptionStatus({ shop, shopId, className }: SubscriptionStatusProps) {
  const router = useRouter()

  if (!shop || !shopId) {
    return null
  }

  const subscription = shop.subscription

  const getDaysRemaining = (): number | null => {
    if (!subscription?.current_period_end) return null

    const endDate = new Date(subscription.current_period_end)
    const now = new Date()
    const diffTime = endDate.getTime() - now.getTime()
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    return diffDays >= 0 ? diffDays : 0
  }

  const daysRemaining = getDaysRemaining()
  const hasActiveSubscription = subscription?.status === 'active' && daysRemaining !== null && daysRemaining > 0

  const handlePaymentClick = () => {
    router.push(`/rnp/${shopId}/payment`)
  }

  const formatDaysText = (days: number): string => {
    if (days === 1) {
      return '1 день'
    }
    if (days >= 2 && days <= 4) {
      return `${days} дня`
    }
    return `${days} дней`
  }

  // Trial ещё не активирован — скрываем блок (активация происходит автоматически при подключении).
  if (shop.trial_used === false) {
    return null
  }

  const isTrialUsed = shop.trial_used === true

  if (isTrialUsed) {
    if (!hasActiveSubscription || daysRemaining === null || daysRemaining <= 0) {
      return (
        <div className={cn('flex items-center gap-2', className)}>
          <Button
            variant="default"
            size="sm"
            onClick={handlePaymentClick}
            className="gap-1.5"
          >
            <CreditCard className="h-3.5 w-3.5" />
            Купить подписку
          </Button>
        </div>
      )
    }

    if (daysRemaining >= 1 && daysRemaining < 3) {
      const daysText = formatDaysText(daysRemaining)

      return (
        <div className={cn('flex items-center gap-2', className)}>
          <div className="flex items-center gap-1.5 text-sm" style={{ color: 'rgb(185, 28, 28)' }}>
            <Calendar className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{daysText}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePaymentClick}
            className="gap-1.5"
          >
            <CreditCard className="h-3.5 w-3.5" />
            Продлить
          </Button>
        </div>
      )
    }

    if (daysRemaining >= 3 && daysRemaining <= 10) {
      return (
        <div className={cn('flex items-center gap-2', className)}>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{formatDaysText(daysRemaining)}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePaymentClick}
            className="gap-1.5"
          >
            <CreditCard className="h-3.5 w-3.5" />
            Продлить
          </Button>
        </div>
      )
    }

    if (daysRemaining > 10) {
      return (
        <div className={cn('flex items-center gap-2', className)}>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{formatDaysText(daysRemaining)}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePaymentClick}
            className="gap-1.5"
          >
            <CreditCard className="h-3.5 w-3.5" />
            Продлить
          </Button>
        </div>
      )
    }
  }

  if (!hasActiveSubscription || daysRemaining === null || daysRemaining > 30) {
    return null
  }

  if (daysRemaining >= 10 && daysRemaining <= 30) {
    return null
  }

  if (daysRemaining <= 0) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Button
          variant="default"
          size="sm"
          onClick={handlePaymentClick}
          className="gap-1.5"
        >
          <CreditCard className="h-3.5 w-3.5" />
          Купить подписку
        </Button>
      </div>
    )
  }

  if (daysRemaining >= 1 && daysRemaining < 3) {
    const daysText = formatDaysText(daysRemaining)

    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className="flex items-center gap-1.5 text-sm" style={{ color: 'rgb(185, 28, 28)' }}>
          <Calendar className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{daysText}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handlePaymentClick}
          className="gap-1.5"
        >
          <CreditCard className="h-3.5 w-3.5" />
          Продлить
        </Button>
      </div>
    )
  }

  if (daysRemaining >= 3 && daysRemaining <= 10) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Calendar className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{formatDaysText(daysRemaining)}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handlePaymentClick}
          className="gap-1.5"
        >
          <CreditCard className="h-3.5 w-3.5" />
          Продлить
        </Button>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Button
        variant="default"
        size="sm"
        onClick={handlePaymentClick}
        className="gap-1.5"
      >
        <CreditCard className="h-3.5 w-3.5" />
        Купить подписку
      </Button>
    </div>
  )
}
